import type { Landmark } from './types';

/**
 * Landmark positions sit cleanly inside their parent region's wash
 * circle (centered on each region vertex at radius 330 from 500,500;
 * wash radius 105). Multi-region landmarks (e.g. aftercare springs is
 * referenced by servant, sensualist, and little in their `landmarks`
 * arrays) are physically placed once — in their primary region — and
 * appear as "directory" entries in the drawer for the others.
 *
 * Region centres used here:
 *   servant     (500, 170)
 *   devotee     (786, 335)
 *   sensualist  (786, 665)
 *   primal      (500, 830)
 *   brat        (214, 665)
 *   little      (214, 335)
 *
 * Term-kind landmarks (consent fountain, drop meadow, subspace grove,
 * vetting watchtower) live *between* regions on the highways — they
 * belong to no single archetype.
 */
export const LANDMARKS: Landmark[] = [
  {
    id: 'negotiation-library',
    name: 'the negotiation library',
    kind: 'practice',
    cx: 500,
    cy: 200,
    brief: 'where conversations are had before scenes',
    long:
      'a circular building with reading rooms and small alcoves. visitors come here to talk about limits, safewords, and what is wanted. the library is not glamorous; it is the bedrock that makes everything else safe.',
    region: 'servant',
  },
  {
    id: 'safeword-bell-tower',
    name: 'the safeword bell tower',
    kind: 'practice',
    cx: 500,
    cy: 800,
    brief: 'rings to mark a pause or an end',
    long:
      'a small bronze bell at the top of a short tower. anyone may pull the rope; the sound is allowed, expected, costless. the tower stands at the edge of every play space — visible, always reachable. its presence is the practice.',
    region: 'primal',
  },
  {
    id: 'aftercare-springs',
    name: 'the aftercare springs',
    kind: 'practice',
    cx: 760,
    cy: 645,
    brief: 'warm pools to return to after intensity',
    long:
      'a cluster of warm pools fed by underground heat. after a scene, partners walk here together. water, warmth, slow conversation or no conversation, food at the edge. this place is non-negotiable; what happened before is incomplete without it.',
    region: 'sensualist',
  },
  {
    id: 'collar-cathedral',
    name: 'the collar cathedral',
    kind: 'ritual',
    cx: 756,
    cy: 308,
    brief: 'where commitment is marked with a symbol',
    long:
      'a quiet cathedral with stained-glass windows of small ordinary things — keys, knots, doorways. couples come here to mark commitment with a collar — training, considered, or formal. the ceremony is invented for each pair; the cathedral provides only the silence.',
    region: 'devotee',
  },
  {
    id: 'threshold-bell',
    name: 'the threshold bell',
    kind: 'ritual',
    cx: 816,
    cy: 318,
    brief: 'marks when the dynamic is on or off',
    long:
      'a small handbell kept near the door of the temple. when struck, it marks the moment the dynamic begins — or ends. some pairs use one; some pairs use both. the bell is yours; the sound is the threshold.',
    region: 'devotee',
  },
  {
    id: 'contract-scriptorium',
    name: 'the contract scriptorium',
    kind: 'ritual',
    cx: 790,
    cy: 372,
    brief: 'where agreements are written down',
    long:
      'a long room with desks and ink. couples write their agreements here — the shape of the dynamic, the limits, the exit terms, the expectations. contracts are not legally binding; they are emotionally binding, which is heavier. writing things down clarifies what wanting actually meant.',
    region: 'devotee',
  },
  {
    id: 'rope-shrine',
    name: 'the rope shrine',
    kind: 'practice',
    cx: 815,
    cy: 690,
    brief: 'where the craft of binding is studied',
    long:
      'a small wooden shrine kept by practitioners of rope. patterns hang on the walls; new ones are taught here. the work is half art, half safety; the difference between beautiful and dangerous is measured in millimeters.',
    region: 'sensualist',
  },
  {
    id: 'munch-tavern',
    name: 'the munch tavern',
    kind: 'practice',
    cx: 200,
    cy: 690,
    brief: 'where the community meets without play',
    long:
      'a tavern on the edge of the brat market. munches are social meet-ups for kinky people in vanilla spaces — no play, just conversation. for newcomers, this is often the first door. for experienced practitioners, the place to vet, to reference, to stay woven into the wider community.',
    region: 'brat',
  },
  {
    id: 'consent-fountain',
    name: 'the consent fountain',
    kind: 'term',
    cx: 500,
    cy: 500,
    brief: 'at the very center of the atlas',
    long:
      'a fountain at the geometric center of the country. all roads pass near it. its water is the substrate everything else is made of: freely given, informed, ongoing permission — revocable at any time, by either person, for any reason. without it, nothing else here is valid.',
  },
  {
    id: 'drop-meadow',
    name: 'the drop meadow',
    kind: 'term',
    cx: 370,
    cy: 745,
    brief: 'where the body settles after intensity',
    long:
      'a wide meadow visible only on certain days. drop is the low mood that arrives hours or days after a heavy scene — for either partner. the meadow exists. you cannot prevent it from arriving, only prepare for it. eat, sleep, reach out, wait it through.',
  },
  {
    id: 'subspace-grove',
    name: 'the subspace grove',
    kind: 'term',
    cx: 370,
    cy: 245,
    brief: 'a small grove where the mind goes quiet',
    long:
      'a small grove off the main road. subspace is the non-ordinary state of consciousness that sometimes arrives during deep submission. it cannot be summoned; it arrives or it does not. it is not the goal of practice; when it appears, it is a gift.',
  },
  {
    id: 'vetting-watchtower',
    name: 'the vetting watchtower',
    kind: 'term',
    cx: 850,
    cy: 500,
    brief: 'the practice of careful welcoming',
    long:
      'a tall watchtower on the highway between regions. travelers stop here before opening doors to a new partner: references, conversations, time, a slow build. defensiveness about being vetted is its own information.',
  },
];
